import { Flex } from '@chakra-ui/react'
import { IndicatorLegendButton } from '@components/IndicatorLegend'
import { ScreenContext, useApp, useAppModals, useAppRole } from '@hb/react'
import { AppName } from '@hb/shared'
import { useContext, useMemo } from 'react'
import { PregnancyCalcButton } from '../../../charting/PregnancyCalculator/PregnancyCalculatorPanel'
import { SearchBarProvider } from '../../../contexts/SearchBarContext'
import { DataListTab } from '../../../types/data'
import { DataList } from '../../DataList'
import { PatientProfile } from '../../Users'
import { PageContainer } from '../../Views/PageContainer'
import { getAdminAssessmentTab } from './admin'
import { getClaimTab } from './claims'
import { getPatientTab } from './patients'
import { deliveredOnDescSort, getPracticeAssessmentTab } from './practice'

const getTabs = (
  appName: AppName,
  onCreatePatientClick: () => void,
  midwifeId?: string,
  superAdmin?: boolean,
): Record<string, DataListTab<any>> => {
  const tabs: Record<string, DataListTab<any>> = {}

  if (!midwifeId) {
    tabs['All Assessments'] = getAdminAssessmentTab(null, false)
    tabs.Inquiry = getAdminAssessmentTab('Inquiry', false)
    tabs['Auth / Task List'] = getAdminAssessmentTab('Authorization', false)
    tabs['Consent Forms'] = getAdminAssessmentTab('Consent Forms', false)
    // tabs['Office Visits'] = getVisitsTab(appName, null, null, null, true)
    tabs['Send Claim'] = getAdminAssessmentTab('Send Claim', false)
    tabs.Claims = getClaimTab(null, false)
    tabs.Complaints = getClaimTab(null, true)
    tabs.Complete = getAdminAssessmentTab('Complete', false)
    tabs.Archived = getAdminAssessmentTab(null, true)
  } else {
    tabs['All Pregnancies'] = getPracticeAssessmentTab(midwifeId, null, false, !!superAdmin)
    tabs.Prospective = getPracticeAssessmentTab(midwifeId, 'Inquiry', false, !!superAdmin)
    tabs.Current = getPracticeAssessmentTab(midwifeId, 'Current', false, !!superAdmin)
    tabs['Post Partum'] = getPracticeAssessmentTab(
      midwifeId,
      'Postpartum',
      false,
      !!superAdmin,
      deliveredOnDescSort,
    )
    tabs.GYN = getPracticeAssessmentTab(midwifeId, 'Gyn', false, !!superAdmin)
    tabs.Other = getPracticeAssessmentTab(midwifeId, 'Other', false, !!superAdmin)
    // tabs['Office Visits'] = getVisitsTab(appName, midwifeId, null, null, true)
    tabs.Discharged = getPracticeAssessmentTab(midwifeId, 'Discharged', false, !!superAdmin)
    if (superAdmin) {
      tabs.Complete = getPracticeAssessmentTab(midwifeId, 'Complete', false, superAdmin)
    }
    tabs.Archived = getPracticeAssessmentTab(midwifeId, null, true, !!superAdmin)
  }
  tabs.Patients = getPatientTab(onCreatePatientClick, midwifeId)

  return tabs
}

const AssessmentsListSearchBarRightAddOn = ({ midwifeId }: { midwifeId?: string }) => {
  if (!midwifeId) return null
  return (
    <Flex alignItems="center" gap={1} pr={1}>
      <PregnancyCalcButton />
      <IndicatorLegendButton />
    </Flex>
  )
}

export const AssessmentsList = ({ midwifeId }: { midwifeId?: string }) => {
  const { appName } = useApp()
  const { open: openInviteModal } = useAppModals(s => s.invitePatient)
  // const [profileVersion] = useState<'v1' | 'v2'>('v1')
  const role = useAppRole()
  const tabs = useMemo(
    () =>
      getTabs(
        appName,
        () => {
          openInviteModal()
        },
        midwifeId,
        role === 'super-admin',
      ),
    [midwifeId, role, openInviteModal, appName],
  )

  const basePath = useMemo(() => (midwifeId ? '/' : '/admin'), [midwifeId])

  const { contentHeight } = useContext(ScreenContext)
  return (
    <PageContainer fullWidth noFooter>
      <SearchBarProvider>
        <DataList
          searchBarRightAddOn={<AssessmentsListSearchBarRightAddOn midwifeId={midwifeId} />}
          height={contentHeight - 70}
          rootPath={basePath}
          tabs={tabs}
        />
        <PatientProfile inList basePath={basePath} />
      </SearchBarProvider>
    </PageContainer>
  )
}
